/**
 * PiiWarningToast — toast dédié à la détection PII live pendant une session.
 *
 * 2 niveaux visuels (alignés backend) :
 *   • 1 — rappel doux (bleu, 5s)
 *   • 2 — rappel renforcé (rouge, 8s, non-auto-dismissible sauf clic)
 */
import React, { useEffect, useState } from 'react';
import { ShieldAlert, Info, X } from 'lucide-react';

const STYLES = {
  1: {
    bg: 'bg-blue-600/95',
    border: 'border-blue-400/40',
    icon: Info,
    autoDismissMs: 5000,
    label: 'Rappel',
  },
  2: {
    bg: 'bg-red-600/95',
    border: 'border-red-400/40',
    icon: ShieldAlert,
    autoDismissMs: 8000,
    label: 'Rappel important',
  },
};

export default function PiiWarningToast({ detection, onDismiss }) {
  const [visible, setVisible] = useState(Boolean(detection));

  useEffect(() => {
    if (!detection) { setVisible(false); return; }
    setVisible(true);
    const cfg = STYLES[detection.rappel_level] || STYLES[1];
    const t = setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, cfg.autoDismissMs);
    return () => clearTimeout(t);
  }, [detection, onDismiss]);

  if (!detection || !visible) return null;
  const cfg = STYLES[detection.rappel_level] || STYLES[1];
  const Icon = cfg.icon;

  return (
    <div
      data-testid="pii-warning-toast"
      data-level={detection.rappel_level}
      className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[calc(100vw-2rem)] pointer-events-auto animate-[slideDown_0.3s_ease-out]"
      style={{ animation: 'slideDown 0.3s ease-out' }}
    >
      <div
        className={`${cfg.bg} ${cfg.border} backdrop-blur-xl border rounded-2xl shadow-2xl p-4 text-white`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80 mb-0.5">
              {cfg.label} • Confidentialité
            </p>
            <p className="text-sm leading-snug">{detection.message}</p>
          </div>
          <button
            onClick={() => { setVisible(false); onDismiss?.(); }}
            className="flex-shrink-0 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            data-testid="pii-warning-dismiss"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
